/**
 * Payment Gateway v1 — error code -> user sentence (contract §9).
 *
 * `gw.error.<CODE>` lives in the `common` i18n namespace (one shared set for every
 * surface — wallet, console, checkout — since the closed code set in `contract.js` is
 * the same wire regardless of who is looking at it). An unrecognised code (a future
 * addition to the backend's set that this build predates) falls back to `gw.error.UNKNOWN`
 * rather than printing the raw code or the server's English `message`.
 */
import { ERROR_CODES } from "./contract";

/**
 * @param {?string} code one of ERROR_CODES, or null/unknown
 * @param {function} t a translator bound to useT("common")
 * @returns {string}
 */
export function gatewayErrorText(code, t) {
  const known = code && ERROR_CODES.includes(code);
  return t(known ? `gw.error.${code}` : "gw.error.UNKNOWN");
}

/**
 * The reason to show for a failure that carries BOTH a code and a stored message.
 *
 * Payout rows fail two ways. A gateway refusal fails with a code from the closed set and
 * no useful prose — that renders as its localised sentence. A bank leg fails with
 * Stripe's own `failure_code` (`account_closed`, `no_account`, …), which is NOT in the
 * closed set and never will be — Stripe owns that vocabulary — but Stripe also gives us
 * `failure_message`, a human sentence. Printing the generic "something went wrong" over a
 * reason we were literally handed is the G1 defect. Precedence is code-first: a known code
 * is a sentence we have translated, and must not be displaced by an English diagnostic.
 *
 * @param {?string} code
 * @param {?string} message the provider's own human-readable reason, if stored
 * @param {function} t a translator bound to useT("common")
 * @returns {?string} null when there is no failure at all
 */
export function gatewayFailureText(code, message, t) {
  if (code && ERROR_CODES.includes(code)) return t(`gw.error.${code}`);
  if (message) return message;
  return code ? t("gw.error.UNKNOWN") : null;
}

export default gatewayErrorText;
